import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import OtpInput from './OtpInput';
import Button from '../../../components/Button/Button';
import styles from './Login.module.css';
import resendOtpStyles from '../../../components/ResendOtp/ResendOtp.module.css';
import './LoginOtpVerification.css';

/**
 * OTP Verification Screen for Email/Password Login
 * User enters 4-digit code received via EMAIL or SMS
 * Reuses OtpInput and ResendOtp components from mobile OTP flow
 */
const LoginOtpVerification = ({
  channel = 'EMAIL',
  onOtpVerified = () => {},
  onResendOtp = () => {},
  loading = false,
  error = null,
  attemptsRemaining = 5,
  resendAvailableInSeconds = 0,
  hideTrustDeviceOption = false,
}) => {
  const [otp, setOtp] = useState('');
  const [otpResetKey, setOtpResetKey] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);
  const [attemptsError, setAttemptsError] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(Math.max(0, Number(resendAvailableInSeconds) || 0));

  useEffect(() => {
    // Timer is controlled by backend `resendAvailableInSeconds`.
    setResendSeconds(Math.max(0, Number(resendAvailableInSeconds) || 0));
  }, [resendAvailableInSeconds]);

  useEffect(() => {
    if (resendSeconds <= 0) {
      return undefined;
    }

    const interval = setInterval(() => {
      setResendSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendSeconds]);

  useEffect(() => {
    // If parent API error is present, clear local validation error to avoid stale duplicates.
    if (error) {
      setAttemptsError(null);
    }
  }, [error]);

  const handleOtpChange = (otpValue) => {
    setOtp(otpValue);
    if (attemptsError) {
      setAttemptsError(null);
    }
  };

  const handleOtpComplete = (completedOtp) => {
    setOtp(completedOtp);
  };

  const handleSubmit = async () => {
    if (!otp || otp.length !== 4) {
      setAttemptsError('Please enter all 4 digits');
      return;
    }

    onOtpVerified(otp, hideTrustDeviceOption ? false : trustDevice);
  };

  const handleResendClick = async () => {
    if (loading || isResending || resendSeconds > 0) {
      return;
    }

    setIsResending(true);
    try {
      const result = await onResendOtp();
      setOtp('');
      // Remount OtpInput so the visible boxes clear together with `otp`.
      setOtpResetKey((prev) => prev + 1);
      setAttemptsError(null);
      if (result?.resendAvailableInSeconds != null) {
        setResendSeconds(Math.max(0, Number(result.resendAvailableInSeconds) || 0));
      }
    } catch (err) {
      // API error is surfaced via parent `error` prop to avoid duplicate messages.
    } finally {
      setIsResending(false);
    }
  };

  const isDisabled = loading || !(otp.length >= 4);
  const otpLabel = `Enter the 4-digit code sent to your ${channel === 'SMS' ? 'phone' : 'email'}`;
  const showResendTimer = resendSeconds > 0;
  const resendTimeText = `${Math.floor(resendSeconds / 60)}:${String(resendSeconds % 60).padStart(2, '0')}`;
  const lowAttemptsMessage = attemptsRemaining > 0 && attemptsRemaining <= 2
    ? `${attemptsRemaining} attempt${attemptsRemaining !== 1 ? 's' : ''} remaining`
    : null;
  const displayError = attemptsError || error || lowAttemptsMessage;

  return (
    <div className="flex gap-3 flex-col w-full">
      <OtpInput
        key={otpResetKey}
        onChange={handleOtpChange}
        onComplete={handleOtpComplete}
        length={4}
        classInput="bg-[#2C0C37] border-2 border-darkDry text-center text-4xl"
        label={otpLabel}
        handleSubmit={handleSubmit}
      />

      {showResendTimer ? (
        <button
          disabled
          type="button"
          className={`${resendOtpStyles.resendOtpButton} ${resendOtpStyles.resendOtpButtonTimer}`}
        >
          I didn’t receive a code (
          {resendTimeText}
)
        </button>
      ) : (
        <button
          onClick={handleResendClick}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleResendClick();
            }
          }}
          type="button"
          className={`${resendOtpStyles.resendOtpButton} ${resendOtpStyles.resendOtpButtonActive}`}
          disabled={loading || isResending}
        >
          I didn’t receive a code
        </button>
      )}

      {!hideTrustDeviceOption && (
        <div className="trust-device-checkbox">
          <input
            type="checkbox"
            checked={trustDevice}
            onChange={(e) => setTrustDevice(e.target.checked)}
            disabled={loading}
            aria-label="Trust this device for future logins"
          />
          <span className="checkbox-label">
            Trust this device for future logins
          </span>
        </div>
      )}

      <div className="w-full mt-4">
        <Button
          isLoading={loading}
          disabled={isDisabled}
          customContainerStyles={`${styles.buttonContainer} ${
            isDisabled ? '!bg-disabled' : ''
          } !block w-full bg-button text-white text-center py-2 h-11`}
          handleSubmit={isDisabled ? null : handleSubmit}
          label="Continue"
        />
      </div>

      {displayError && (
        <p className={styles.hasError}>{displayError}</p>
      )}
    </div>
  );
};

LoginOtpVerification.propTypes = {
  channel: PropTypes.oneOf(['EMAIL', 'SMS']),
  onOtpVerified: PropTypes.func,
  onResendOtp: PropTypes.func,
  loading: PropTypes.bool,
  error: PropTypes.string,
  attemptsRemaining: PropTypes.number,
  resendAvailableInSeconds: PropTypes.number,
  hideTrustDeviceOption: PropTypes.bool,
};

LoginOtpVerification.defaultProps = {
  channel: 'EMAIL',
  onOtpVerified: () => {},
  onResendOtp: () => {},
  loading: false,
  error: null,
  attemptsRemaining: 5,
  resendAvailableInSeconds: 0,
  hideTrustDeviceOption: false,
};

export default LoginOtpVerification;
